// src/components/main/Hero.js
import React, { useState } from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SecondaryLoader from '../loaders/SecondaryLoader';

/**
 * Hero — high-fashion luxury background with refined story box
 */
export default function Hero() {
  const theme = useTheme();
  const navigate = useNavigate();
  const accent = '#b0a990';

  // loader state
  const [loading, setLoading] = useState(false);

  const handleClick = () => {
    setLoading(true);
    setTimeout(() => {
      navigate('/products');
    }, 2000); // show loader for at least 2s
  };

  return (
    <>
      <AnimatePresence>
        {loading && <SecondaryLoader key="secondary-loader" />}
      </AnimatePresence>

      <Box
        component={motion.section}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, ease: 'easeOut' }}
        sx={{
          position: 'relative',
          minHeight: { xs: '75vh', md: '90vh' },
          backgroundImage: `url('https://brokepiggytoken.xyz/wp-content/uploads/2025/07/OlivegrovePuglia.jpeg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          '&::after': {
            content: "''",
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.25)',
            zIndex: 1,
          },
        }}
      >
        {/* Artisanal story box, lower-left */}
        <Box
          sx={{
            position: 'absolute',
            bottom: { xs: '24px', md: '64px' },
            left: { xs: '24px', md: '64px' },
            zIndex: 2,
            width: { xs: 'calc(100% - 48px)', md: '360px' },
          }}
        >
          <Box
            component={motion.div}
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
            sx={{
              backdropFilter: 'blur(8px)',
              backgroundColor: 'rgba(0,0,0,0.7)',
              borderRadius: 2,
              p: { xs: 2.5, md: 3.5 },
              boxShadow: '0 8px 32px rgba(0,0,0,0.8)',
            }}
          >
            {/* Title with bottom accent line */}
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Merriweather, serif',
                fontWeight: 300,
                mb: 1,
                fontSize: { xs: '1rem', md: '1.25rem' },
                color: '#fff',
                letterSpacing: '0.5px',
                borderBottom: `2px solid ${accent}`,
                display: 'inline-block',
                pb: 0.5,
              }}
            >
              Pure Heritage
            </Typography>

            {/* Subtext */}
            <Typography
              variant="body2"
              sx={{
                mt: 2,
                mb: 3,
                fontFamily: 'Roboto, sans-serif',
                fontSize: { xs: '0.8rem', md: '0.9rem' },
                lineHeight: 1.6,
                color: '#eee',
              }}
            >
              Our ancient olive groves have thrived for centuries, each harvest embodying the essence of
              Marrakech’s sun-kissed terroir and artisanal mastery.
            </Typography>

            {/* CTA Button */}
            <Button
              component={motion.button}
              whileHover={{ scale: 1.02 }}
              onClick={handleClick}
              sx={{
                backgroundColor: 'transparent',
                color: '#fff',
                border: `1px solid ${accent}`,
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 300,
                textTransform: 'none',
                px: 3,
                py: 1,
                fontSize: '0.75rem',
                borderRadius: 1,
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: accent,
                  color: '#000',
                },
              }}
            >
              Discover Our Olive Oil 
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
}
